import { Container } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './scenes/BaseScene';
import { ANIMATION } from './constants/theme';
import type { LayoutMetrics } from './LayoutManager';

export type SceneKey =
  | 'title'
  | 'warrior-selection'
  | 'ai-difficulty'
  | 'room'
  | 'coin-flip'
  | 'battle'
  | 'game-over';

/**
 * Manages scene lifecycle and transitions
 */
export class ScreenManager {
  public readonly container: Container;
  private scenes: Map<SceneKey, BaseScene> = new Map();
  private currentScene: SceneKey | null = null;
  private isTransitioning = false;

  constructor() {
    this.container = new Container();
  }

  /**
   * Register a scene with a key
   */
  registerScene(key: SceneKey, scene: BaseScene): void {
    this.scenes.set(key, scene);
    this.container.addChild(scene.container);
  }

  /**
   * Get current scene key
   */
  getCurrentScene(): SceneKey | null {
    return this.currentScene;
  }

  /**
   * Get a registered scene
   */
  getScene<T extends BaseScene>(key: SceneKey): T | undefined {
    return this.scenes.get(key) as T | undefined;
  }

  /**
   * Transition to a new scene with fade animation
   */
  async transitionTo(key: SceneKey): Promise<void> {
    if (this.isTransitioning) return;
    if (this.currentScene === key) return;

    const nextScene = this.scenes.get(key);
    if (!nextScene) {
      console.error(`Scene not found: ${key}`);
      return;
    }

    this.isTransitioning = true;

    // Fade out current scene
    if (this.currentScene) {
      const currentSceneObj = this.scenes.get(this.currentScene);
      if (currentSceneObj) {
        await gsap.to(currentSceneObj.container, {
          alpha: 0,
          duration: ANIMATION.FADE_DURATION,
        });
        currentSceneObj.onExit();
      }
    }

    // Prepare next scene
    nextScene.container.alpha = 0;
    nextScene.onEnter();

    // Fade in next scene
    await gsap.to(nextScene.container, {
      alpha: 1,
      duration: ANIMATION.FADE_DURATION,
    });

    this.currentScene = key;
    this.isTransitioning = false;
  }

  /**
   * Refresh the current scene by calling onExit and onEnter
   * Useful when staying on same scene but state has changed (e.g., P1 → P2 warrior selection)
   */
  async refreshCurrentScene(): Promise<void> {
    if (!this.currentScene || this.isTransitioning) return;

    const scene = this.scenes.get(this.currentScene);
    if (!scene) return;

    this.isTransitioning = true;

    // Fade out
    await gsap.to(scene.container, {
      alpha: 0,
      duration: ANIMATION.FADE_DURATION,
    });

    // Rebuild the scene
    scene.onExit();
    scene.onEnter();

    // Fade in
    await gsap.to(scene.container, {
      alpha: 1,
      duration: ANIMATION.FADE_DURATION,
    });

    this.isTransitioning = false;
  }

  /**
   * Immediately switch to a scene without animation
   */
  switchTo(key: SceneKey): void {
    const nextScene = this.scenes.get(key);
    if (!nextScene) {
      console.error(`Scene not found: ${key}`);
      return;
    }

    // Hide current scene
    if (this.currentScene) {
      const currentSceneObj = this.scenes.get(this.currentScene);
      if (currentSceneObj) {
        currentSceneObj.container.alpha = 0;
        currentSceneObj.onExit();
      }
    }

    // Show next scene
    nextScene.container.alpha = 1;
    nextScene.onEnter();
    this.currentScene = key;
  }

  /**
   * Update current scene
   */
  update(delta: number): void {
    if (this.currentScene) {
      const scene = this.scenes.get(this.currentScene);
      scene?.onUpdate(delta);
    }
  }

  /**
   * Notify scenes of resize with new layout metrics
   */
  onResize(metrics: LayoutMetrics): void {
    for (const scene of this.scenes.values()) {
      scene.onResize(metrics);
    }
  }

  /**
   * Dispose all scenes
   */
  dispose(): void {
    for (const scene of this.scenes.values()) {
      scene.dispose();
    }
    this.scenes.clear();
    this.container.destroy({ children: true });
  }
}
